import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getOrderClaimForm, setOrderClaimForm } from 'domains/order/Order.store';
import {
  getTextMandatory,
  isHidden,
  useIsClaimedQuantityComputed,
  useIsReceivedReferenceDifferentFromOrdered,
} from 'pages/MyOrdersPage/OrderClaims/claimForm.utils';
import { getAmountLabel } from 'pages/MyOrdersPage/OrderClaims/claimFormData';
import { FormOption, makeKey, RULES_DATA } from 'pages/MyOrdersPage/OrderClaims/claimFormRulesData';
import { FlexRow, QuantityBubble } from 'pages/MyOrdersPage/OrderClaims/OrderClaims.styled';
import { Flex, Input, InputNumber, Text } from 'UI';

interface OtherSectionProps {
  onQuantityClaimedTouchedChange?: (isTouched: boolean) => void;
}

const FieldWrapper = ({
  visible,
  children,
  maxWidth = 200,
}: {
  visible: boolean;
  children: React.ReactNode;
  maxWidth?: number;
}) => {
  if (!visible) {
    return null;
  }

  return (
    <Flex direction={'column'} maxWidth={maxWidth}>
      {children}
    </Flex>
  );
};

const OtherSection = ({ onQuantityClaimedTouchedChange }: OtherSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const claimForm = useSelector(getOrderClaimForm);
  const isClaimedQuantityComputed = useIsClaimedQuantityComputed();
  const isReceivedRefValid = useIsReceivedReferenceDifferentFromOrdered();
  const [isQuantityClaimedTouched, setIsQuantityClaimedTouched] = useState(false);

  const rulesData =
    RULES_DATA[
      makeKey({
        formType: claimForm.formType,
        reason: claimForm.reason,
        option: claimForm.option,
      })
    ];

  const isQuantityInvoicedVisible = !isHidden(rulesData?.quantityInvoiced) && claimForm.quantityInvoiced != null;

  const isReferenceNumberSingleVisible =
    !isHidden(rulesData?.referenceNumberOrdered) && isHidden(rulesData?.referenceNumberReceived);

  const isReferenceNumberDualVisible =
    !isHidden(rulesData?.referenceNumberOrdered) && !isHidden(rulesData?.referenceNumberReceived);

  const updateOrderNumber = useCallback(
    (orderNumber: string) => {
      dispatch(setOrderClaimForm({ ...claimForm, orderNumber }));
    },
    [dispatch, claimForm],
  );

  const areAllFieldsHidden = (fields: Array<FormOption | undefined>) => {
    for (const field of fields) {
      if (!isHidden(field)) {
        return false;
      }
    }
    return true;
  };

  const updateInvoiceNumber = useCallback(
    (invoiceNumber: string) => {
      dispatch(setOrderClaimForm({ ...claimForm, invoiceNumber }));
    },
    [dispatch, claimForm],
  );

  const updateInvoiceLineNumber = useCallback(
    (invoiceLineNumber: number | undefined) => {
      dispatch(setOrderClaimForm({ ...claimForm, invoiceLineNumber }));
    },
    [dispatch, claimForm],
  );

  const updateReferenceNumber = useCallback(
    (referenceNumberOrdered: string) => {
      dispatch(setOrderClaimForm({ ...claimForm, referenceNumberOrdered }));
    },
    [dispatch, claimForm],
  );

  const updateReferenceNumberReceived = useCallback(
    (referenceNumberReceived: string) => {
      dispatch(setOrderClaimForm({ ...claimForm, referenceNumberReceived }));
    },
    [dispatch, claimForm],
  );

  const updateAmount = useCallback(
    (amount: number | undefined) => {
      if (amount !== undefined && amount !== null) {
        dispatch(setOrderClaimForm({ ...claimForm, amount }));
      }
    },
    [dispatch, claimForm],
  );

  const updateSoldToPartyId = useCallback(
    (soldToPartyId: string) => {
      dispatch(setOrderClaimForm({ ...claimForm, soldToPartyId }));
    },
    [dispatch, claimForm],
  );

  const updateShipToPartyId = useCallback(
    (shipToPartyId: string) => {
      dispatch(setOrderClaimForm({ ...claimForm, shipToPartyId }));
    },
    [dispatch, claimForm],
  );

  const updateQuantityReceived = useCallback(
    (quantityReceived: number | undefined) => {
      if (quantityReceived !== undefined && quantityReceived !== null) {
        const isAdditionalPart = ['Y01', 'Y01N'].includes(claimForm.reason);
        const quantityInvoiced = claimForm.quantityInvoiced ?? 0;

        const quantityClaimed = isAdditionalPart
          ? quantityReceived - quantityInvoiced
          : quantityInvoiced - quantityReceived;

        dispatch(
          setOrderClaimForm({
            ...claimForm,
            quantityReceived,
            ...(claimForm.quantityInvoiced != null && { quantityClaimed }),
          }),
        );
      }
    },
    [dispatch, claimForm],
  );

  const updateQuantityClaimed = useCallback(
    (quantityClaimed: number | undefined) => {
      setIsQuantityClaimedTouched(true);
      onQuantityClaimedTouchedChange?.(true);

      if (quantityClaimed !== undefined && quantityClaimed !== null) {
        dispatch(setOrderClaimForm({ ...claimForm, quantityClaimed }));
      }
    },
    [dispatch, claimForm, onQuantityClaimedTouchedChange],
  );

  const allSectionFields = [
    rulesData?.orderNumber,
    rulesData?.invoiceNumber,
    rulesData?.invoiceLineNumber,
    rulesData?.referenceNumberOrdered,
    rulesData?.quantityOrdered,
    rulesData?.referenceNumberReceived,
    rulesData?.quantityReceived,
    rulesData?.quantityClaimed,
    rulesData?.amount,
    rulesData?.quantityInvoiced,
    rulesData?.soldToPartyId,
    rulesData?.shipToPartyId,
  ];

  const isOtherSectionHidden = areAllFieldsHidden(allSectionFields);

  useEffect(() => {
    if (claimForm.reason === 'Y08' && claimForm.quantityInvoiced !== undefined && claimForm.quantityInvoiced !== null) {
      dispatch(setOrderClaimForm({ ...claimForm, quantityClaimed: claimForm.quantityInvoiced }));
    }
  }, [claimForm.reason, claimForm.quantityInvoiced, claimForm, dispatch]);

  useEffect(() => {
    setIsQuantityClaimedTouched(false);
    onQuantityClaimedTouchedChange?.(false);
  }, [claimForm.formType, claimForm.reason, claimForm.option, onQuantityClaimedTouchedChange]);

  if (isOtherSectionHidden) {
    return null;
  }

  const getOrderNumberLabel = () =>
    claimForm.reason === 'Y07'
      ? t('order.order_detail.package_number', 'Package Number')
      : t('order.order_detail.order_number', 'Order Number');

  const renderQuantityClaimedInput = () => {
    if (isClaimedQuantityComputed) {
      return (
        <QuantityBubble type={'blue'}>
          <Text type={'text_white'} disableGutter>
            {claimForm.quantityClaimed ?? '-'}
          </Text>
        </QuantityBubble>
      );
    }

    return (
      <InputNumber
        id={'input-quantity-claimed'}
        bordered
        min={0}
        max={99999}
        value={
          isQuantityClaimedTouched || claimForm.quantityClaimed !== 0
            ? claimForm.quantityClaimed ?? undefined
            : undefined
        }
        onChangeDebounced={updateQuantityClaimed}
      />
    );
  };

  return (
    <>
      <Text type="heading3" disableGutter noWrap>
        {t('common.other', 'Other')}
      </Text>
      <FlexRow gap={30}>
        <FieldWrapper visible={!isHidden(rulesData?.orderNumber)}>
          <Text type="base" noWrap>
            {getTextMandatory(getOrderNumberLabel(), rulesData?.orderNumber)}
          </Text>
          <Input bordered initialValue={claimForm.orderNumber} onChangeDebounced={updateOrderNumber} />
        </FieldWrapper>

        <FieldWrapper visible={!isHidden(rulesData?.invoiceNumber)}>
          <Text type="base" noWrap>
            {getTextMandatory(t('order.invoice_number', 'Invoice number'), rulesData?.invoiceNumber)}
          </Text>
          <Input bordered initialValue={claimForm.invoiceNumber} onChangeDebounced={updateInvoiceNumber} />
        </FieldWrapper>

        <FieldWrapper visible={!isHidden(rulesData?.invoiceLineNumber)}>
          <Text type="base" noWrap>
            {getTextMandatory(t('order.invoice_line_number', 'Invoice Line number'), rulesData?.invoiceLineNumber)}
          </Text>
          <InputNumber
            bordered
            initialValue={claimForm.invoiceLineNumber}
            min={0}
            max={999999}
            onChangeDebounced={updateInvoiceLineNumber}
          />
        </FieldWrapper>

        <FieldWrapper visible={isReferenceNumberSingleVisible}>
          <Text type="base" noWrap>
            {getTextMandatory(
              t('order.reference_table.reference_number', 'Reference number'),
              rulesData?.referenceNumberOrdered,
            )}
          </Text>
          <Input bordered initialValue={claimForm.referenceNumberOrdered} onChangeDebounced={updateReferenceNumber} />
        </FieldWrapper>

        <FieldWrapper visible={!isHidden(rulesData?.amount)}>
          <Text type="base" noWrap>
            {getTextMandatory(getAmountLabel(t, claimForm.reason), rulesData?.amount)}
          </Text>
          <InputNumber
            id={'input-amount'}
            bordered
            min={0}
            max={99999}
            value={claimForm.amount}
            onChangeDebounced={updateAmount}
          />
        </FieldWrapper>
      </FlexRow>
      <FlexRow gap={30}>
        <FieldWrapper visible={!isHidden(rulesData?.soldToPartyId)}>
          <Text type="base" noWrap>
            {getTextMandatory(t('order.sold_to_party_id', 'Sold to party ID'), rulesData?.soldToPartyId)}
          </Text>
          <Input bordered initialValue={claimForm.soldToPartyId} onChangeDebounced={updateSoldToPartyId} />
        </FieldWrapper>

        <FieldWrapper visible={!isHidden(rulesData?.shipToPartyId)}>
          <Text type="base" noWrap>
            {getTextMandatory(t('order.ship_to_party_id', 'Ship to party ID'), rulesData?.shipToPartyId)}
          </Text>
          <Input bordered initialValue={claimForm.shipToPartyId} onChangeDebounced={updateShipToPartyId} />
        </FieldWrapper>
      </FlexRow>
      <FlexRow gap={30}>
        <FieldWrapper visible={isReferenceNumberDualVisible}>
          <Text type="base" noWrap>
            {getTextMandatory(
              t('order.reference_table.ordered_reference', 'Ordered Reference'),
              rulesData?.referenceNumberOrdered,
            )}
          </Text>
          <Input bordered initialValue={claimForm.referenceNumberOrdered} onChangeDebounced={updateReferenceNumber} />
        </FieldWrapper>

        <FieldWrapper visible={!isHidden(rulesData?.referenceNumberReceived)}>
          <Text type="base" noWrap>
            {getTextMandatory(
              t('order.reference_table.received_reference', 'Received Reference'),
              rulesData?.referenceNumberReceived,
            )}
          </Text>
          <Input
            bordered
            initialValue={claimForm.referenceNumberReceived}
            onChangeDebounced={updateReferenceNumberReceived}
          />
        </FieldWrapper>
      </FlexRow>
      {!isReceivedRefValid && (
        <Text type="base" color="red">
          {t(
            'order.claim.received_reference_same_as_ordered',
            'The received reference must be different from the ordered reference',
          )}
        </Text>
      )}
      <FlexRow gap={30}>
        <FieldWrapper visible={!isHidden(rulesData?.quantityReceived)}>
          <Text type="base" noWrap>
            {getTextMandatory(
              t('order.reference_table.received_quantity', 'Received Quantity'),
              rulesData?.quantityReceived,
            )}
          </Text>
          <InputNumber
            id={'input-quantity-received'}
            bordered
            min={0}
            max={99999}
            value={claimForm.quantityReceived ?? undefined}
            onChangeDebounced={updateQuantityReceived}
          />
        </FieldWrapper>

        <FieldWrapper visible={isQuantityInvoicedVisible}>
          <Text type="base" noWrap>
            {getTextMandatory(
              t('order.reference_table.invoiced_quantity', 'Invoiced Quantity'),
              rulesData?.quantityInvoiced,
            )}
          </Text>
          <QuantityBubble type={'grey'}>
            <Text type={'text_white'} disableGutter>
              {claimForm.quantityInvoiced}
            </Text>
          </QuantityBubble>
        </FieldWrapper>

        <FieldWrapper visible={!isHidden(rulesData?.quantityClaimed)}>
          <Text type="base" noWrap>
            {getTextMandatory(
              t('order.reference_table.claimed_quantity', 'Claimed Quantity'),
              rulesData?.quantityClaimed,
            )}
          </Text>
          {renderQuantityClaimedInput()}
        </FieldWrapper>
      </FlexRow>
    </>
  );
};

export default OtherSection;
